import React, {
  Suspense,
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import {
  useGLTF,
  useTexture,
  Loader,
  Environment,
  useFBX,
  useAnimations,
  OrthographicCamera,
} from "@react-three/drei";
import { MeshStandardMaterial } from "three/src/materials/MeshStandardMaterial";
import { LinearEncoding, sRGBEncoding } from "three/src/constants";
import { LineBasicMaterial, MeshPhysicalMaterial, Vector2 } from "three";
import ReactAudioPlayer from "react-audio-player";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import createAnimation from "./converter";
import blinkData from "./blendDataBlink.json";
import SpeechRecognition, {
  useSpeechRecognition,
} from "react-speech-recognition";
import "./App.css";
import * as THREE from "three";
import axios from "axios";
import initialData from "./initial.json";
import initialAudio from "./speech-ss50y.mp3"; // Ensure this file exists in src

const _ = require("lodash");

const host = "https://aibackend.ksofttechnologies.com"; // Backend host for API calls

function Avatar({
  avatar_url,
  speak,
  setSpeak,
  setAudioSource,
  playing,
  clips,
  setClips,
  setMorphTargets,
}) {
  const gltf = useGLTF(avatar_url);
  const textures = useTexture([
    "/images/body.webp",
    "/images/eyes.webp",
    "/images/teeth_diffuse.webp",
    "/images/body_specular.webp",
    "/images/body_roughness.webp",
    "/images/body_normal.webp",
    "/images/teeth_normal.webp",
    "/images/h_color.webp",
    "/images/h_alpha.webp",
    "/images/h_normal.webp",
    "/images/h_roughness.webp",
    "/images/tshirt_diffuse.webp",
    "/images/tshirt_normal.webp",
    "/images/tshirt_roughness.webp",
  ]);
  const mixer = useMemo(
    () => new THREE.AnimationMixer(gltf.scene),
    [gltf.scene]
  );
  const idleFbx = useFBX("/idle.fbx");
  const { clips: idleClips } = useAnimations(idleFbx.animations);
  const speechActionsRef = useRef([]);

  const [error, setError] = useState(null);

  useEffect(() => {
    if (!gltf) {
      setError("Failed to load avatar model.");
      toast.error("Failed to load avatar model.");
      return;
    }
    if (!textures.every((t) => t)) {
      setError("Failed to load textures.");
      toast.error("Failed to load textures.");
      return;
    }
    if (!idleFbx) {
      setError("Failed to load idle animation.");
      toast.error("Failed to load idle animation.");
      return;
    }

    let morphTargetDictionaryBody = null;
    let morphTargetDictionaryLowerTeeth = null;
    try {
      gltf.scene.traverse((node) => {
        if (node.name.includes("Body")) {
          morphTargetDictionaryBody = node.morphTargetDictionary;
        }
        if (node.name.includes("TeethLower")) {
          morphTargetDictionaryLowerTeeth = node.morphTargetDictionary;
        }
      });
      setMorphTargets({
        body: morphTargetDictionaryBody,
        lowerTeeth: morphTargetDictionaryLowerTeeth,
      });
    } catch (err) {
      console.error("Error accessing morph targets:", err);
      setError("Error processing avatar.");
      toast.error("Error processing avatar.");
    }
  }, [gltf, textures, idleFbx, setMorphTargets]);

  const [
    bodyTexture,
    eyesTexture,
    teethTexture,
    bodySpecularTexture,
    bodyRoughnessTexture,
    bodyNormalTexture,
    teethNormalTexture,
    hairTexture,
    hairAlphaTexture,
    hairNormalTexture,
    hairRoughnessTexture,
    tshirtDiffuseTexture,
    tshirtNormalTexture,
    tshirtRoughnessTexture,
  ] = textures;

  _.each(
    [
      bodyTexture,
      eyesTexture,
      teethTexture,
      teethNormalTexture,
      bodySpecularTexture,
      bodyRoughnessTexture,
      bodyNormalTexture,
      tshirtDiffuseTexture,
      tshirtNormalTexture,
      tshirtRoughnessTexture,
      hairAlphaTexture,
      hairNormalTexture,
      hairRoughnessTexture,
    ],
    (t) => {
      if (t) {
        t.encoding = sRGBEncoding;
        t.flipY = false;
      }
    }
  );

  if (bodyNormalTexture) bodyNormalTexture.encoding = LinearEncoding;
  if (tshirtNormalTexture) tshirtNormalTexture.encoding = LinearEncoding;
  if (teethNormalTexture) teethNormalTexture.encoding = LinearEncoding;
  if (hairNormalTexture) hairNormalTexture.encoding = LinearEncoding;

  try {
    gltf.scene.traverse((node) => {
      if (
        node.type === "Mesh" ||
        node.type === "LineSegments" ||
        node.type === "SkinnedMesh"
      ) {
        node.castShadow = true;
        node.receiveShadow = true;
        node.frustumCulled = false;

        if (node.name.includes("Body")) {
          node.castShadow = true;
          node.receiveShadow = true;
          node.material = new MeshPhysicalMaterial();
          node.material.map = bodyTexture;
          node.material.roughness = 1.7;
          node.material.roughnessMap = bodyRoughnessTexture;
          node.material.normalMap = bodyNormalTexture;
          node.material.normalScale = new Vector2(0.6, 0.6);
          node.material.envMapIntensity = 0.8;
        }

        if (node.name.includes("Eyes")) {
          node.material = new MeshStandardMaterial();
          node.material.map = eyesTexture;
          node.material.roughness = 0.1;
          node.material.envMapIntensity = 0.5;
        }

        if (node.name.includes("Brows")) {
          node.material = new LineBasicMaterial({ color: 0x000000 });
          node.material.linewidth = 1;
          node.material.opacity = 0.5;
          node.material.transparent = true;
          node.visible = false;
        }

        if (node.name.includes("Teeth")) {
          node.receiveShadow = true;
          node.castShadow = true;
          node.material = new MeshStandardMaterial();
          node.material.roughness = 0.1;
          node.material.map = teethTexture;
          node.material.normalMap = teethNormalTexture;
          node.material.envMapIntensity = 0.7;
        }

        if (node.name.includes("Hair")) {
          node.material = new MeshStandardMaterial();
          node.material.map = hairTexture;
          node.material.alphaMap = hairAlphaTexture;
          node.material.normalMap = hairNormalTexture;
          node.material.roughnessMap = hairRoughnessTexture;
          node.material.transparent = true;
          node.material.depthWrite = false;
          node.material.side = 2;
          node.material.color.setHex(0x000000);
          node.material.envMapIntensity = 0.3;
        }

        if (node.name.includes("TSHIRT")) {
          node.material = new MeshStandardMaterial();
          node.material.map = tshirtDiffuseTexture;
          node.material.roughnessMap = tshirtRoughnessTexture;
          node.material.normalMap = tshirtNormalTexture;
          node.material.color.setHex(0xffffff);
          node.material.envMapIntensity = 0.5;
        }
      }
    });
  } catch (err) {
    setError("Error processing avatar.");
    toast.error("Error processing avatar.");
  }

  try {
    idleClips[0].tracks = _.filter(idleClips[0].tracks, (track) => {
      return (
        track.name.includes("Head") ||
        track.name.includes("Neck") ||
        track.name.includes("Spine2")
      );
    });

    idleClips[0].tracks = _.map(idleClips[0].tracks, (track) => {
      if (track.name.includes("Head")) {
        track.name = "head.quaternion";
      }
      if (track.name.includes("Neck")) {
        track.name = "neck.quaternion";
      }
      if (track.name.includes("Spine")) {
        track.name = "spine2.quaternion";
      }
      return track;
    });
  } catch (err) {
    setError("Error processing idle animations.");
    toast.error("Error processing idle animations.");
  }

  useEffect(() => {
    if (!idleClips[0]) return;

    try {
      let idleClipAction = mixer.clipAction(idleClips[0]);
      idleClipAction.play();

      let blinkClip = createAnimation(
        blinkData,
        { HG_Body: gltf.scene.getObjectByName("Body")?.morphTargetDictionary },
        "HG_Body"
      );
      let blinkAction = mixer.clipAction(blinkClip);
      blinkAction.play();
    } catch (err) {
      console.error("Error setting up animations:", err);
      toast.error("Error setting up animations.");
    }
  }, [mixer, idleClips, gltf.scene]);

  useEffect(() => {
    if (playing === false || !clips.length) {
      speechActionsRef.current.forEach((action) => {
        action.stop();
      });
      speechActionsRef.current = [];
      return;
    }

    try {
      speechActionsRef.current.forEach((action) => {
        action.stop();
      });
      speechActionsRef.current = [];

      _.each(clips, (clip) => {
        let clipAction = mixer.clipAction(clip);
        clipAction.setLoop(THREE.LoopOnce);
        clipAction.play();
        speechActionsRef.current.push(clipAction);
      });
    } catch (err) {
      console.error("Error playing clips:", err);
      toast.error("Error playing speech animation.");
    }
  }, [playing, clips, mixer]);

  useFrame((state, delta) => {
    try {
      mixer.update(delta);
    } catch (err) {
      console.error("Error updating mixer:", err);
    }
  });

  if (error) {
    return null;
  }

  return (
    <group name="avatar">
      <primitive object={gltf.scene} dispose={null} />
    </group>
  );
}

const fetchGeminiResponse = async (msg, signal) => {
  const prompt = `Role:
You are the Head of Sales at Ksoft Technologies, headquartered in India. Note: Pronounce "Ksoft" as "Kaysoft" in spoken responses. You specialize in delivering tailored software development, digital marketing, and IT consulting solutions to clients across various industries worldwide. Your team is known for its agility, technical excellence, and client-first approach.

Tone:
Maintain a semi-formal, friendly, and confident demeanor—think of a sharp, approachable sales lead. Use clear, conversational English. Avoid jargon and buzzwords; focus on clarity, helpfulness, and professionalism.

Response Guidelines:
- Provide summarized, practical, and clear answers—avoid excessive detail unless explicitly requested.
- If the user inquires about an organization or company:
  • Mention specific names, titles, or contact details only if they are clearly stated in the user's message.
  • If no names are provided, do not speculate or mention any names or placeholder contact information.
- If the user asks about any person at Ksoft, direct them to the company leadership or contact form on the website if relevant.
- For general questions, highlight Ksoft's value proposition and how it typically assists clients in addressing similar challenges.
- When asked about Ksoft’s website (e.g., blog, services, portfolio), include accurate URLs and a brief summary of the section. Use https://ksofttechnologies.com as the base URL.

Ksoft Technologies Overview:
Note: Pronounce "Ksoft" as "Kaysoft" in spoken responses.

Experience:
A trusted technology partner with a strong team of developers and digital experts delivering scalable, efficient, and user-friendly software.

Core Services:
- Custom Software Development: Full-cycle development in web, mobile, and desktop platforms (React, Next.js, Node.js, PHP, Flutter, etc.).
- Mobile App Development: High-performance Android and iOS apps with modern UI/UX.
- Digital Marketing: SEO, SEM, social media marketing, and content strategies to drive visibility and engagement.
- IT Consulting: Architecture reviews, process optimization, technical strategy, and project rescue.
- AI & Automation: AI-powered chatbots, analytics, RPA, and custom workflow automation.
- E-commerce Solutions: Custom e-commerce platforms with seamless integrations and user-centric features.

Industries Served:
E-commerce, Logistics, Real Estate, Education, Health & Wellness, Technology, and more.

Client Focus:
Startups, SMBs, and Enterprises seeking reliable tech partners to scale their operations and innovate.

Client Testimonials:
- Clients consistently appreciate Ksoft's responsiveness, technical know-how, and dedication to delivery timelines.
- Successful collaborations have led to long-term partnerships and product evolutions.

Contact Information:
Website: https://ksofttechnologies.com  
Email: info@ksofttechnologies.com

Now, respond to the user's message below. Keep it concise, clear, and helpful, reflecting your role and tone.

User Message:
"${msg}"
`;

  const start = new Date();

  const apiKey = process.env.REACT_APP_GEMINI_API_KEY || "";
  const apiUrl =
    "https://generativelanguage.googleapis.com/v1beta/models/gemini-1.5-flash-latest:generateContent";
  const requestBody = {
    contents: [
      {
        parts: [
          {
            text: prompt,
          },
        ],
      },
    ],
  };

  try {
    const response = await fetch(`${apiUrl}?key=${apiKey}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
      signal,
    });

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    const data = await response.json();
    const text = data?.candidates?.[0]?.content?.parts?.[0]?.text ?? "";
    const timeTaken = new Date() - start;
    return { text, timeTaken };
  } catch (error) {
    if (error.name === "AbortError") {
      console.log("Gemini fetch aborted");
    } else {
      console.error(`Error calling Gemini AI API: ${error.message}`);
    }
    throw error;
  }
};

function ErrorBoundary({ children }) {
  const [error, setError] = useState(null);

  useEffect(() => {
    const errorHandler = (event) => {
      console.error("Uncaught error:", event.error);
      setError("An error occurred while rendering the 3D scene.");
      toast.error("Failed to render 3D scene. Please refresh.");
    };
    window.addEventListener("error", errorHandler);
    return () => window.removeEventListener("error", errorHandler);
  }, []);

  if (error) {
    return <div style={{ color: "red", padding: "10px" }}>{error}</div>;
  }

  return children;
}

function App() {
  const isIOS =
    /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const [chats, setChats] = useState([
    { msg: "Hi there! How can I assist you today?", who: "bot", exct: "0" },
  ]);
  const [text, setText] = useState(
    "Hello I am K Bot, your AI assistant. You can ask anything about Kaysoft."
  );
  const [msg, setMsg] = useState("");
  const [exct, setexct] = useState("");
  const [load, setLoad] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [speak, setSpeak] = useState(false);
  const [audioSource, setAudioSource] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [clips, setClips] = useState([]);
  const [morphTargets, setMorphTargets] = useState({
    body: null,
    lowerTeeth: null,
  });
  const [isClearing, setIsClearing] = useState(false);
  const [mediaStream, setMediaStream] = useState(null);
  const [audioContextResumed, setAudioContextResumed] = useState(false);

  const audioPlayer = useRef();
  const abortControllerRef = useRef(null);
  const inputRef = useRef(null);

  const playAudio = async (audioEl, retries = 20, delay = 1500) => {
    if (!audioEl || !audioEl.src) {
      console.error("Audio element or source not ready:", {
        audioEl,
        src: audioEl?.src,
      });
      toast.error("Audio player not initialized or source missing.", {
        autoClose: 8000,
      });
      return false;
    }
    for (let i = 0; i < retries; i++) {
      try {
        console.log(`Attempting audio playback (${i + 1}/${retries}):`, audioEl.src);
        await resumeAudioContext(); // Ensure AudioContext is resumed
        await audioEl.play();
        console.log("Audio playback started successfully:", audioEl.src);
        return true;
      } catch (err) {
        console.warn(`Audio playback attempt ${i + 1} failed:`, err.message, {
          src: audioEl.src,
          error: err,
        });
        if (err.name === "NotAllowedError") {
          console.warn("Autoplay blocked; user gesture may be required");
        }
        if (i < retries - 1) {
          await new Promise((resolve) => setTimeout(resolve, delay));
        }
      }
    }
    console.error("All audio playback attempts failed:", audioEl.src);
    return false;
  };

  // Initialize AudioContext for Safari
  const resumeAudioContext = async () => {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    const audioContext = new AudioContext();
    if (audioContext.state === "suspended") {
      await audioContext.resume();
      console.log("AudioContext resumed for Safari");
      setAudioContextResumed(true);
    }
    return audioContext;
  };

  // Simulate user gesture to resume AudioContext
  useEffect(() => {
    const handleUserInteraction = async () => {
      if (!audioContextResumed) {
        await resumeAudioContext();
      }
    };

    window.addEventListener("click", handleUserInteraction);
    window.addEventListener("touchstart", handleUserInteraction);
    return () => {
      window.removeEventListener("click", handleUserInteraction);
      window.removeEventListener("touchstart", handleUserInteraction);
    };
  }, [audioContextResumed]);

  useEffect(() => {
    console.log("Initial useEffect running with morphTargets:", morphTargets);
    if (
      morphTargets.body &&
      morphTargets.lowerTeeth &&
      Object.keys(morphTargets.body).length > 0 &&
      Object.keys(morphTargets.lowerTeeth).length > 0
    ) {
      console.log("Morph Targets Valid, setting up initial message and audio");
      const initialMessage =
        "Hello I am K Bot, your AI assistant. You can ask anything about Kaysoft.";
      setChats((chats) => [
        ...chats,
        { msg: initialMessage, who: "me" },
        { msg: initialMessage, who: "bot", exct: "0" },
      ]);
      setText(initialMessage);
      setexct("0");

      const blendData = initialData.blendData;

      if (!blendData) {
        console.error("Invalid initial blend data:", initialData);
        toast.error("Failed to load initial animation data.", {
          autoClose: 8000,
        });
        return;
      }

      const fullAudioURL = initialAudio; // Imported MP3
      console.log("Using imported audio URL:", fullAudioURL);

      let newClips = [];
      try {
        newClips = [
          createAnimation(blendData, morphTargets.body, "HG_Body"),
          createAnimation(blendData, morphTargets.lowerTeeth, "HG_TeethLower"),
        ].filter((clip) => clip !== null);
        console.log("Animation Clips Created:", newClips);
      } catch (err) {
        console.error("Error creating animation clips:", err);
        toast.error("Failed to create lip-sync animation.", {
          autoClose: 8000,
        });
        return;
      }

      setClips(newClips);
      setSpeak(true);
      setAudioSource(fullAudioURL);

      // Delay playback significantly for Safari
      setTimeout(() => {
        const player = audioPlayer.current?.audioEl?.current;
        if (player) {
          player.src = fullAudioURL;
          console.log("Attempting to play initial audio:", fullAudioURL);

          resumeAudioContext().then(() => {
            playAudio(player, 20, 1500)
              .then((success) => {
                if (success) {
                  setPlaying(true);
                } else {
                  console.error("Initial audio playback failed after all retries");
                  toast.error(
                    isSafari
                      ? "Failed to play audio in Safari. Please ensure Safari Settings > Websites > Auto-Play is set to 'Allow All Auto-Play' for this site, then refresh."
                      : "Failed to play audio. Please try refreshing the page.",
                    {
                      autoClose: 10000,
                      position: "top-center",
                    }
                  );
                }
              })
              .catch((err) => {
                console.error("Initial audio playback error:", err);
                toast.error(
                  isSafari
                    ? "Failed to play audio in Safari. Please ensure Safari Settings > Websites > Auto-Play is set to 'Allow All Auto-Play' for this site, then refresh."
                    : "Failed to play audio: " + err.message,
                  {
                    autoClose: 10000,
                    position: "top-center",
                  }
                );
              });
          });
        } else {
          console.error("Audio player not found");
          toast.error("Audio player not initialized.", {
            autoClose: 8000,
          });
        }
      }, 7000); // Increased delay to 7 seconds
    } else {
      console.warn("Morph Targets Invalid, skipping initial setup:", morphTargets);
    }
  }, [morphTargets, isSafari]);

  const stopCurrentResponse = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    const player = audioPlayer.current?.audioEl?.current;
    if (player) {
      player.pause();
      player.src = "";
    }
    if (isRecording) {
      stopListening();
      resetTranscript();
    }
    setLoad(false);
    setSpeak(false);
    setPlaying(false);
    setAudioSource(null);
    setClips([]);
    setMsg("");
    if (inputRef.current) inputRef.current.value = "";
  };

  const getResponse = async (message) => {
    if (isRecording) {
      stopListening();
    }
    if (!message.trim()) {
      toast.error("Prompt can't be empty.");
      return;
    }
    setChats((chats) => [...chats, { msg: message, who: "me" }]);
    setIsClearing(true);
    setMsg("");
    if (inputRef.current) inputRef.current.value = "";
    setLoad(true);

    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    try {
      const response = await fetchGeminiResponse(message, signal);
      const replyText = response.text;
      const timeTaken = response.timeTaken;

      setText(replyText);
      setexct(timeTaken / 1000);

      const talkResponse = await axios.post(
        `${host}/talk`,
        { text: replyText },
        { signal }
      );
      const { blendData, filename } = talkResponse.data;
      if (!blendData || !filename) {
        throw new Error("Invalid speech response");
      }
      const fullAudioURL = `${host}${filename}`;

      if (morphTargets.body && morphTargets.lowerTeeth) {
        const newClips = [
          createAnimation(blendData, morphTargets.body, "HG_Body"),
          createAnimation(blendData, morphTargets.lowerTeeth, "HG_TeethLower"),
        ].filter((clip) => clip !== null);
        setClips(newClips);
      }
      setSpeak(true);
      setAudioSource(fullAudioURL);

      setTimeout(() => {
        const player = audioPlayer.current?.audioEl?.current;
        if (player) {
          player.src = fullAudioURL;
          resumeAudioContext().then(() => {
            playAudio(player, 20, 1500)
              .then((success) => {
                if (success) {
                  setPlaying(true);
                  setChats((chats) => [
                    ...chats,
                    { msg: replyText, who: "bot", exct: timeTaken / 1000 },
                  ]);
                } else {
                  toast.error(
                    isSafari
                      ? "Failed to play audio response in Safari. Please ensure Safari Settings > Websites > Auto-Play is set to 'Allow All Auto-Play' for this site, then try again."
                      : "Failed to play audio response. Please try again.",
                    {
                      autoClose: 8000,
                    }
                  );
                }
              })
              .catch((err) => {
                console.error("Audio playback error in getResponse:", err);
                toast.error(
                  isSafari
                    ? "Failed to play audio response in Safari. Please ensure Safari Settings > Websites > Auto-Play is set to 'Allow All Auto-Play' for this site, then try again."
                    : "Failed to play audio response: " + err.message,
                  {
                    autoClose: 8000,
                  }
                );
              });
          });
        }
      }, 100);
    } catch (err) {
      if (err.name === "AbortError") {
        console.log("Response aborted");
      } else {
        console.error("Error in getResponse:", err);
        toast.error("Something went wrong.");
      }
    } finally {
      setLoad(false);
      setSpeak(false);
      setIsClearing(false);
      abortControllerRef.current = null;
      setMsg("");
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  useEffect(() => {
    const chatBox = document.querySelector(".chat-box");
    if (chatBox) {
      chatBox.scrollTop = chatBox.scrollHeight;
    }
  }, [chats]);

  function playerEnded(e) {
    setAudioSource(null);
    setSpeak(false);
    setPlaying(false);
    setClips([]);
  }

  const {
    transcript,
    browserSupportsSpeechRecognition,
    listening,
    resetTranscript,
  } = useSpeechRecognition();

  const startListening = () => {
    console.log("startListening called, browserSupportsSpeechRecognition:", browserSupportsSpeechRecognition);
    if (!browserSupportsSpeechRecognition) {
      console.error("Speech recognition not supported");
      toast.error("Voice recognition not supported by browser.");
      return;
    }
    stopCurrentResponse();
    setMsg("");
    if (inputRef.current) inputRef.current.value = "";
    resetTranscript();
    console.log("Checking available audio devices...");
    navigator.mediaDevices
      .enumerateDevices()
      .then((devices) => {
        const audioDevices = devices.filter(
          (device) => device.kind === "audioinput"
        );
        if (audioDevices.length === 0) {
          console.error("No audio input devices found");
          toast.error("No microphone found. Please connect a microphone.");
          return;
        }
        console.log("Requesting microphone access...");
        setTimeout(() => {
          navigator.mediaDevices
            .getUserMedia({ audio: true })
            .then((stream) => {
              console.log("Microphone access granted, stream:", stream);
              setMediaStream(stream);
              SpeechRecognition.startListening({ continuous: true });
              setIsRecording(true);
              toast.info("Recording started");
            })
            .catch((err) => {
              console.error("Microphone access error:", {
                name: err.name,
                message: err.message,
                stack: err.stack,
              });
              let errorMessage = "Error accessing microphone.";
              if (err.name === "NotAllowedError") {
                errorMessage =
                  "Microphone access denied. Please allow microphone access in your browser settings. " +
                  (isSafari
                    ? "Go to Safari > Settings > Websites > Microphone and allow access."
                    : "Check your browser's Site Settings > Microphone.");
              } else if (err.name === "NotFoundError") {
                errorMessage = "No microphone found. Please connect a microphone.";
              } else {
                errorMessage = `Error accessing microphone: ${err.message}`;
              }
              toast.error(errorMessage, { autoClose: 8000 });
            });
        }, isSafari ? 1000 : 0);
      })
      .catch((err) => {
        console.error("Error enumerating devices:", err);
        toast.error("Error detecting microphone: " + err.message, {
          autoClose: 8000,
        });
      });
  };

  const stopListening = useCallback(() => {
    if (listening || isRecording) {
      console.log("Stopping mic at", new Date().toISOString());
      SpeechRecognition.stopListening();
      SpeechRecognition.abortListening();
      if (mediaStream) {
        mediaStream.getTracks().forEach((track) => track.stop());
        setMediaStream(null);
      }
      setIsRecording(false);
      setMsg("");
      if (inputRef.current) inputRef.current.value = "";
      resetTranscript();
      console.log("Mic stopped at", new Date().toISOString());
    }
  }, [listening, resetTranscript, mediaStream, isRecording]);

  const handleMicClick = () => {
    console.log("Mic button clicked, isRecording:", isRecording);
    if (isRecording) {
      stopListening();
      const currentMsg = msg;
      if (currentMsg.trim()) {
        setIsClearing(true);
        stopCurrentResponse();
        setMsg("");
        if (inputRef.current) inputRef.current.value = "";
        getResponse(currentMsg);
        setTimeout(() => {
          setIsClearing(false);
        }, 100);
      } else {
        setIsClearing(false);
      }
    } else {
      startListening();
    }
  };

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && isRecording) {
        stopListening();
        toast.info("Recording stopped as tab is inactive.");
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isRecording, stopListening]);

  useEffect(() => {
    if (!isClearing && transcript && isRecording && listening) {
      setMsg(transcript);
      if (inputRef.current) inputRef.current.value = transcript;
    }
  }, [transcript, isClearing, isRecording, listening]);

  return (
    <div className="full">
      <ToastContainer
        position="top-left"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />
      <div className="about">
        <img src="/images/icons/logo-icon.png" alt="logo" />
      </div>
      <div className="chat-div">
        <div className="chat-box">
          {chats.map((chat, index) => {
            if (chat.who === "me") {
              return (
                <p key={index} className={chat.who}>
                  {chat.msg}
                </p>
              );
            } else {
              return (
                <p key={index} className={chat.who}>
                  {chat.msg}
                  <span className="time">
                    {"generated in " + chat.exct + "s"}
                  </span>
                </p>
              );
            }
          })}
          {(load || speak) && !playing && (
            <p
              style={{ padding: "5USEDpx", display: "flex", alignItems: "center" }}
            >
              Loading...
            </p>
          )}
        </div>
        <div className="msg-box">
          <div className="msg-box-content">
            <button
              className={`msgbtn ${isRecording ? "recording" : ""}`}
              id="mic"
              onClick={handleMicClick}
            >
              <img src="./images/icons/mic.png" alt="mic" unselectable="on" />
            </button>
            <input
              type="text"
              ref={inputRef}
              value={msg}
              onChange={(e) => {
                if (isRecording) {
                  stopListening();
                }
                setMsg(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const currentMsg = msg;
                  if (isRecording) {
                    stopListening();
                  }
                  if (currentMsg.trim()) {
                    setIsClearing(true);
                    stopCurrentResponse();
                    setMsg("");
                    if (inputRef.current) inputRef.current.value = "";
                    getResponse(currentMsg);
                    setTimeout(() => {
                      setIsClearing(false);
                    }, 100);
                  } else {
                    setMsg("");
                    if (inputRef.current) inputRef.current.value = "";
                    setIsClearing(false);
                  }
                }
              }}
              placeholder="Type here..."
            />
            <button
              className="sendbtn"
              id="send"
              onClick={() => {
                if (load || speak || playing) {
                  stopCurrentResponse();
                } else {
                  const currentMsg = msg;
                  if (isRecording) {
                    stopListening();
                  }
                  if (currentMsg.trim()) {
                    setIsClearing(true);
                    stopCurrentResponse();
                    setMsg("");
                    if (inputRef.current) inputRef.current.value = "";
                    getResponse(currentMsg);
                    setTimeout(() => {
                      setIsClearing(false);
                    }, 100);
                  } else {
                    setMsg("");
                    if (inputRef.current) inputRef.current.value = "";
                    setIsClearing(false);
                  }
                }
              }}
            >
              {load || speak ? (
                <div className="spinner"></div>
              ) : playing ? (
                <img src="./images/icons/stop.png" alt="stop" />
              ) : (
                <img src="./images/icons/send.png" alt="send" />
              )}
            </button>
          </div>
        </div>
      </div>
      <ReactAudioPlayer
        src={audioSource}
        ref={audioPlayer}
        preload="auto"
        onEnded={playerEnded}
      />
      <ErrorBoundary>
        <Suspense fallback={<div>Loading 3D scene...</div>}>
          <Canvas dpr={isIOS ? 1 : 2}>
            <OrthographicCamera
              makeDefault
              zoom={550}
              position={[0, 1.65, 1]}
            />
            <Environment
              background={false}
              files="/images/photo_studio_loft_hall_1k.hdr"
            />
            <Bg />
            <Avatar
              avatar_url="/model.glb"
              speak={speak}
              setSpeak={setSpeak}
              setAudioSource={setAudioSource}
              playing={playing}
              clips={clips}
              setClips={setClips}
              setMorphTargets={setMorphTargets}
            />
          </Canvas>
        </Suspense>
      </ErrorBoundary>
      <Loader dataInterpolation={(p) => `Loading... please wait`} />
    </div>
  );
}

function Bg() {
  return (
    <mesh position={[0, 1.5, -4]} scale={[1.2, 1.2, 1.2]}>
      <planeBufferGeometry />
      <meshBasicMaterial color={0x075353} />
    </mesh>
  );
}

export default App;